/**
 * SWIM Brand Gradients — Frozen Identity
 *
 * These gradients define SWIM's visual identity. Never change these without
 * a deliberate design decision. Each gradient has a name, its CSS value,
 * and the surfaces it lives on.
 *
 * Usage in inline styles:
 *   style={{ background: GRADIENTS.pacer.main }}
 *
 * Usage in Tailwind (for directional utilities like bg-gradient-to-br):
 *   Use the tailwind tokens (ocean-300, ocean-400, etc.) directly.
 *   These constants are for inline style usage or JS-driven animations.
 */
export const GRADIENTS = {
  /**
   * Ocean — teal-to-blue sweep.
   * Identity: search fields, clear buttons, action accents.
   * Tailwind equivalent: from-ocean-300 to-ocean-400
   */
  ocean: 'linear-gradient(135deg, #60a5c8 0%, #267db8 100%)',

  /**
   * Splash — pink blossom → lavender → pale sky.
   * Identity: app loading screen background.
   * Source: SplashScreen.tsx
   */
  splash: 'linear-gradient(135deg, #f9e4e8 0%, #e8dff5 40%, #d5e8f5 100%)',

  /**
   * Pacer — warm amber-to-lavender sweep (animated).
   * Identity: the Pacer orb button and its modal header.
   * Multiple keyframe variants cycle on the orb; use `pacer.main` as a static version.
   * Source: PacerOrb.tsx
   */
  pacer: {
    main:    'linear-gradient(135deg, #fde68a 0%, #f59e0b 35%, #fb923c 65%, #e8dff5 100%)',
    frameA:  'linear-gradient(135deg, #fde68a 0%, #f59e0b 35%, #fb923c 65%, #e8dff5 100%)',
    frameB:  'linear-gradient(220deg, #fed7aa 0%, #fbbf24 30%, #e8dff5 60%, #a5f3fc 100%)',
    frameC:  'linear-gradient(315deg, #fde68a 0%, #fb923c 40%, #f59e0b 70%, #fed7aa 100%)',
    /** Spinning conic beam used for the animated border ring. */
    conic:   'conic-gradient(from 0deg, transparent 0%, transparent 70%, #fde68a 78%, #f59e0b 86%, #fb923c 92%, #e8dff5 97%, #a5f3fc 99%, transparent 100%)',
    /** Radial inner glow. */
    glow:    'radial-gradient(circle, rgba(251,191,36,0.25) 0%, transparent 70%)',
  },

  /**
   * Auth vignette — four-corner radial gradient overlay.
   * Identity: sign-in, sign-up, forgot-password page chrome dimming.
   * Layered as backgroundImage with multiple values (comma-separated).
   * Source: SignInPage.tsx, SignUpPage.tsx, ForgotPasswordPage.tsx
   */
  authVignette: [
    'radial-gradient(ellipse at 0% 0%, rgba(253,230,138,0.35) 0%, transparent 55%)',
    'radial-gradient(ellipse at 100% 100%, rgba(165,243,252,0.30) 0%, transparent 55%)',
    'radial-gradient(ellipse at 100% 0%, rgba(232,223,245,0.25) 0%, transparent 45%)',
    'radial-gradient(ellipse at 0% 100%, rgba(251,146,60,0.20) 0%, transparent 40%)',
  ].join(', '),
} as const
